import { fetchWithAuth, BASE_URL } from './client';
import { logError } from '../../shared/utils/logger';

/**
 * SSO (Single Sign-On) API Service
 * Handles SSO authentication and configuration management
 *
 * SECURITY NOTES:
 * - Only authorization code flow is supported (tokens exchanged server-side)
 * - Legacy token-in-URL flow is DEPRECATED and rejected by frontend
 * - Backend sets httpOnly secure cookies for token storage (XSS protection)
 * - All sensitive operations require server-side JWT validation
 * - Organization context is validated server-side from the JWT, not from client parameters
 */

// ==================== SSO STATE MANAGEMENT (CSRF Protection) ====================

const SSO_STATE_KEY = 'sso_auth_state';
const SSO_STATE_EXPIRY_KEY = 'sso_auth_state_expiry';
const STATE_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Generate a cryptographically random state parameter for CSRF protection
 * @returns {string} Random state string
 */
function generateStateParam() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Store SSO state for validation on callback
 * @param {string} state - The state parameter to store
 */
function storeState(state) {
    const expiry = Date.now() + STATE_EXPIRY_MS;
    sessionStorage.setItem(SSO_STATE_KEY, state);
    sessionStorage.setItem(SSO_STATE_EXPIRY_KEY, expiry.toString());
}

/**
 * Validate and consume SSO state from callback
 * @param {string} returnedState - The state parameter from callback URL
 * @returns {boolean} True if state is valid
 */
export function validateAndConsumeState(returnedState) {
    const storedState = sessionStorage.getItem(SSO_STATE_KEY);
    const expiryStr = sessionStorage.getItem(SSO_STATE_EXPIRY_KEY);

    // Always clear state after validation attempt (one-time use)
    sessionStorage.removeItem(SSO_STATE_KEY);
    sessionStorage.removeItem(SSO_STATE_EXPIRY_KEY);

    if (!storedState || !returnedState) {
        return false;
    }

    // Check expiry
    const expiry = parseInt(expiryStr, 10);
    if (isNaN(expiry) || Date.now() > expiry) {
        logError('SSO state expired');
        return false;
    }

    // Constant-time comparison to prevent timing attacks
    if (storedState.length !== returnedState.length) {
        return false;
    }

    let result = 0;
    for (let i = 0; i < storedState.length; i++) {
        result |= storedState.charCodeAt(i) ^ returnedState.charCodeAt(i);
    }

    return result === 0;
}

// ==================== PUBLIC SSO ROUTES (No Auth Required) ====================

/**
 * Get public SSO configuration for login page
 * GET /api/auth/sso/{slug}/config
 *
 * NOTE: This only returns non-sensitive public config (provider name, enabled status)
 * Sensitive details like client_id are never exposed to the frontend
 *
 * @param {string} slug - Organization slug (e.g., "loanfactory")
 * @returns {Promise<object>} SSO config with provider info
 */
export async function fetchSSOConfig(slug) {
    const response = await fetch(`${BASE_URL}/api/auth/sso/${slug}/config`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) {
        if (response.status === 404) {
            return null; // SSO not configured
        }
        logError('Failed to fetch SSO config', response.status);
        throw new Error('Failed to fetch SSO configuration');
    }
    return response.json();
}

/**
 * Get SSO login URL - redirects user to IdP
 * GET /api/auth/sso/{slug}/init
 *
 * SECURITY: Includes state parameter for CSRF protection
 * The backend should also validate the state parameter on callback
 *
 * @param {string} slug - Organization slug
 * @param {string} redirectTo - Where to redirect after login (optional)
 * @returns {string} The SSO init URL to redirect to
 */
export function getSSOLoginUrl(slug, redirectTo = null) {
    // Generate and store state for CSRF protection
    const state = generateStateParam();
    storeState(state);

    let url = `${BASE_URL}/api/auth/sso/${slug}/init?state=${encodeURIComponent(state)}`;
    if (redirectTo) {
        url += `&redirect_to=${encodeURIComponent(redirectTo)}`;
    }
    return url;
}

/**
 * Initiate SSO login by redirecting to IdP
 *
 * SECURITY: State parameter is automatically included for CSRF protection
 *
 * @param {string} slug - Organization slug
 * @param {string} redirectTo - Where to redirect after login (optional)
 */
export function initiateSSOLogin(slug, redirectTo = null) {
    const url = getSSOLoginUrl(slug, redirectTo);
    window.location.href = url;
}

// ==================== ADMIN SSO MANAGEMENT ROUTES ====================
// SECURITY: All these endpoints require authentication via JWT token.
// The backend MUST validate that:
// 1. The JWT is valid and not expired
// 2. The user identified by the JWT is a member of the specified organization
// 3. The user has admin/owner role in the organization
// Authorization is enforced SERVER-SIDE, not by the frontend.

/**
 * Get SSO configuration for an organization (admin)
 * GET /api/organizations/{org_id}/sso
 *
 * SECURITY: Backend validates user has admin/owner role in org from JWT
 *
 * @param {string} orgId - Organization ID
 * @returns {Promise<object|null>} SSO configuration or null if not configured
 */
export async function fetchOrgSSOConfig(orgId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/sso`);
    if (!response.ok) {
        if (response.status === 404) {
            return null; // SSO not configured
        }
        if (response.status === 403) {
            throw new Error('You do not have permission to access SSO configuration');
        }
        logError('Failed to fetch org SSO config', response.status);
        throw new Error('Failed to fetch SSO configuration');
    }
    return response.json();
}

/**
 * Create SSO configuration for an organization
 * POST /api/organizations/{org_id}/sso
 *
 * SECURITY: Backend validates user has admin/owner role in org from JWT
 *
 * @param {string} orgId - Organization ID
 * @param {object} config - SSO configuration
 * @returns {Promise<object>} Created SSO configuration
 */
export async function createOrgSSOConfig(orgId, config) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/sso`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
    });
    if (!response.ok) {
        if (response.status === 403) {
            throw new Error('You do not have permission to configure SSO');
        }
        const error = await response.json().catch(() => ({}));
        logError('Failed to create SSO config', response.status, error);
        throw new Error(error.detail || 'Failed to create SSO configuration');
    }
    return response.json();
}

/**
 * Update SSO configuration for an organization
 * PUT /api/organizations/{org_id}/sso
 *
 * SECURITY: Backend validates user has admin/owner role in org from JWT
 *
 * @param {string} orgId - Organization ID
 * @param {object} config - Updated SSO configuration
 * @returns {Promise<object>} Updated SSO configuration
 */
export async function updateOrgSSOConfig(orgId, config) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/sso`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
    });
    if (!response.ok) {
        if (response.status === 403) {
            throw new Error('You do not have permission to update SSO configuration');
        }
        const error = await response.json().catch(() => ({}));
        logError('Failed to update SSO config', response.status, error);
        throw new Error(error.detail || 'Failed to update SSO configuration');
    }
    return response.json();
}

/**
 * Delete SSO configuration for an organization
 * DELETE /api/organizations/{org_id}/sso
 *
 * SECURITY: Backend validates user has admin/owner role in org from JWT
 *
 * @param {string} orgId - Organization ID
 * @returns {Promise<void>}
 */
export async function deleteOrgSSOConfig(orgId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/sso`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        if (response.status === 403) {
            throw new Error('You do not have permission to delete SSO configuration');
        }
        logError('Failed to delete SSO config', response.status);
        throw new Error('Failed to delete SSO configuration');
    }
}

/**
 * Test SSO connection by verifying OIDC discovery
 * POST /api/organizations/{org_id}/sso/test
 *
 * SECURITY: Backend validates user has admin/owner role in org from JWT
 *
 * @param {string} orgId - Organization ID
 * @returns {Promise<object>} Test result with endpoints info
 */
export async function testSSOConnection(orgId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/sso/test`, {
        method: 'POST'
    });
    if (!response.ok) {
        if (response.status === 403) {
            throw new Error('You do not have permission to test SSO connection');
        }
        const error = await response.json().catch(() => ({}));
        logError('SSO connection test failed', response.status, error);
        throw new Error(error.detail || 'SSO connection test failed');
    }
    return response.json();
}

/**
 * Enable SSO for an organization
 * POST /api/organizations/{org_id}/sso/enable
 *
 * SECURITY: Backend validates user has admin/owner role in org from JWT
 *
 * @param {string} orgId - Organization ID
 * @returns {Promise<object>} Updated SSO configuration
 */
export async function enableOrgSSO(orgId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/sso/enable`, {
        method: 'POST'
    });
    if (!response.ok) {
        if (response.status === 403) {
            throw new Error('You do not have permission to enable SSO');
        }
        const error = await response.json().catch(() => ({}));
        logError('Failed to enable SSO', response.status, error);
        throw new Error(error.detail || 'Failed to enable SSO');
    }
    return response.json();
}

/**
 * Disable SSO for an organization
 * POST /api/organizations/{org_id}/sso/disable
 *
 * SECURITY: Backend validates user has admin/owner role in org from JWT
 *
 * @param {string} orgId - Organization ID
 * @returns {Promise<object>} Updated SSO configuration
 */
export async function disableOrgSSO(orgId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/sso/disable`, {
        method: 'POST'
    });
    if (!response.ok) {
        if (response.status === 403) {
            throw new Error('You do not have permission to disable SSO');
        }
        const error = await response.json().catch(() => ({}));
        logError('Failed to disable SSO', response.status, error);
        throw new Error(error.detail || 'Failed to disable SSO');
    }
    return response.json();
}

// ==================== SSO HELPERS ====================

/**
 * Extract organization slug from current hostname
 * e.g., "loanfactory.cloudmortgage.io" -> "loanfactory"
 *
 * SECURITY WARNING: This function extracts the slug from client-controlled input (URL).
 * This slug should ONLY be used for:
 * - UI purposes (showing correct branding, login page)
 * - Passing to the backend which will VALIDATE it
 *
 * The slug MUST NOT be used to directly grant access to organization resources.
 * All security-critical operations MUST:
 * 1. Validate the user's JWT token server-side
 * 2. Verify the user is a member of the organization
 * 3. Check the user has appropriate permissions (admin/owner for sensitive ops)
 *
 * The backend should validate that the org_slug matches the user's authorized
 * organizations from their JWT before granting access to any tenant-specific resources.
 *
 * @returns {string|null} Organization slug or null if on main domain
 */
export function getOrgSlugFromHostname() {
    const hostname = window.location.hostname;
    const reservedSubdomains = ['www', 'api', 'app', 'admin', 'staging', 'dev'];

    // Check for subdomain pattern: {slug}.cloudmortgage.io
    const match = hostname.match(/^([a-z0-9-]+)\.cloudmortgage\.io$/i);
    if (match && !reservedSubdomains.includes(match[1].toLowerCase())) {
        return match[1];
    }

    // For local development: {slug}.localhost (e.g., loanfactory.localhost:3000)
    const localMatch = hostname.match(/^([a-z0-9-]+)\.localhost$/i);
    if (localMatch && !reservedSubdomains.includes(localMatch[1].toLowerCase())) {
        return localMatch[1];
    }

    // NOTE: Query param method removed for security - query params are too easily manipulated
    // For local testing, use {slug}.localhost instead

    return null;
}

/**
 * Check if current domain supports SSO
 *
 * @returns {boolean}
 */
export function isSSODomain() {
    return getOrgSlugFromHostname() !== null;
}

/**
 * Get SSO provider display info
 *
 * @param {string} provider - Provider key (okta, azure, google, auth0, generic)
 * @returns {object} Display info with name, icon, color
 */
export function getSSOProviderInfo(provider) {
    const providers = {
        okta: {
            name: 'Okta',
            icon: 'okta',
            color: '#007dc1',
            bgClass: 'bg-[#007dc1]'
        },
        azure: {
            name: 'Microsoft',
            icon: 'microsoft',
            color: '#00a4ef',
            bgClass: 'bg-[#00a4ef]'
        },
        google: {
            name: 'Google',
            icon: 'google',
            color: '#4285f4',
            bgClass: 'bg-[#4285f4]'
        },
        auth0: {
            name: 'Auth0',
            icon: 'auth0',
            color: '#eb5424',
            bgClass: 'bg-[#eb5424]'
        },
        generic: {
            name: 'SSO',
            icon: 'key',
            color: '#6366f1',
            bgClass: 'bg-indigo-500'
        }
    };

    return providers[provider] || providers.generic;
}

/**
 * Parse SSO callback URL parameters
 *
 * SECURITY: Only authorization code flow is supported.
 * The callback URL should contain:
 * - code: Authorization code to exchange server-side
 * - state: CSRF protection parameter
 *
 * Token parameters (access_token, refresh_token) in URL are NOT supported
 * as they expose tokens in browser history and server logs.
 *
 * @returns {object} Parsed callback parameters
 */
export function parseSSOCallback() {
    const urlParams = new URLSearchParams(window.location.search);

    // Log warning if legacy token parameters are detected
    if (urlParams.has('access_token') || urlParams.has('refresh_token')) {
        console.warn(
            'SECURITY WARNING: Tokens in URL parameters detected. ' +
            'This flow is deprecated and not supported. ' +
            'Backend should use authorization code flow only.'
        );
    }

    return {
        // Authorization code (REQUIRED - exchanged server-side for tokens)
        code: urlParams.get('code'),
        // State parameter for CSRF validation (REQUIRED)
        state: urlParams.get('state'),
        // Error information from IdP
        error: urlParams.get('error'),
        errorDescription: urlParams.get('error_description'),
        // Redirect destination after auth
        redirectTo: urlParams.get('redirect_to')
    };
}

/**
 * Exchange authorization code for session (SECURE METHOD)
 *
 * SECURITY: This is the ONLY supported authentication flow:
 * 1. Frontend receives authorization code from IdP callback
 * 2. Frontend sends code to backend with CSRF state
 * 3. Backend exchanges code for tokens with IdP (server-to-server)
 * 4. Backend validates tokens and creates session
 * 5. Backend sets httpOnly secure cookies (not accessible to JS)
 * 6. Backend returns non-sensitive user info for UI
 *
 * IMPORTANT: Tokens are stored in httpOnly cookies by the backend,
 * NOT returned in the response body. This protects against XSS attacks.
 *
 * @param {string} code - Authorization code from IdP
 * @param {string} state - State parameter for CSRF validation
 * @param {string} orgSlug - Organization slug for context
 * @returns {Promise<object>} Session info with user data (tokens in httpOnly cookies)
 */
export async function exchangeSSOCode(code, state, orgSlug) {
    // Validate state parameter locally first (CSRF protection)
    if (!validateAndConsumeState(state)) {
        throw new Error('Invalid or expired authentication state. Please try signing in again.');
    }

    const response = await fetch(`${BASE_URL}/api/auth/sso/${orgSlug}/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // IMPORTANT: Allow backend to set httpOnly cookies
        body: JSON.stringify({ code, state })
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || error.message || 'Failed to complete SSO authentication');
    }

    return response.json();
}

/**
 * @deprecated SECURITY RISK - DO NOT USE
 *
 * This function supported the legacy token-in-URL flow which is now REMOVED.
 * Tokens in URL parameters are exposed in browser history and server logs.
 *
 * Use exchangeSSOCode() with authorization code flow instead.
 * Backend should set httpOnly cookies, not return tokens in response.
 *
 * This function is kept only for reference and will throw an error if called.
 */
export async function validateSSOTokens() {
    throw new Error(
        'SECURITY: Legacy token validation flow is no longer supported. ' +
        'Use authorization code flow (exchangeSSOCode) instead. ' +
        'Contact your administrator if you see this error.'
    );
}

/**
 * SSO status constants
 */
export const SSO_STATUS = {
    DISABLED: 'disabled',
    TESTING: 'testing',
    ENABLED: 'enabled'
};

/**
 * SSO provider constants
 */
export const SSO_PROVIDERS = {
    OKTA: 'okta',
    AZURE: 'azure',
    GOOGLE: 'google',
    AUTH0: 'auth0',
    GENERIC: 'generic'
};

/**
 * Default attribute mappings for each provider
 */
export const DEFAULT_ATTRIBUTE_MAPPINGS = {
    okta: {
        email: 'email',
        first_name: 'given_name',
        last_name: 'family_name'
    },
    azure: {
        email: 'email',
        first_name: 'given_name',
        last_name: 'family_name'
    },
    google: {
        email: 'email',
        first_name: 'given_name',
        last_name: 'family_name'
    },
    auth0: {
        email: 'email',
        first_name: 'given_name',
        last_name: 'family_name'
    },
    generic: {
        email: 'email',
        first_name: 'given_name',
        last_name: 'family_name'
    }
};
